import { FriendLocation } from '../../../types';

export interface MarkerCluster {
  id: string;
  friends: FriendLocation[];
  latitude: number;
  longitude: number;
  center: [number, number];
}

/**
 * Apply a small random offset (jitter) to coordinates to separate overlapping markers
 * The jitter is deterministic based on the friend ID to ensure consistency
 * @param lat Original latitude
 * @param lng Original longitude
 * @param friendId Friend ID for deterministic randomization
 * @returns Jittered coordinates
 */
const applyJitter = (lat: number, lng: number, friendId: number): [number, number] => {
  // Use friend ID as seed for consistent jitter
  const seed = friendId * 9301 + 49297; // Simple pseudo-random based on ID
  const random1 = (seed % 233280) / 233280.0;
  const random2 = ((seed * 2) % 233280) / 233280.0;

  // Apply small offset (0.0003 degrees ≈ 30 meters)
  const jitterAmount = 0.0003;
  const latJitter = (random1 - 0.5) * jitterAmount;
  const lngJitter = (random2 - 0.5) * jitterAmount;

  return [lat + latJitter, lng + lngJitter];
};

/**
 * Check if coordinates are effectively identical (within 0.0001 degrees ≈ 11 meters)
 */
const areCoordinatesIdentical = (lat1: number, lng1: number, lat2: number, lng2: number): boolean => {
  return Math.abs(lat1 - lat2) < 0.0001 && Math.abs(lng1 - lng2) < 0.0001;
};

/**
 * Calculate distance in pixels between two coordinates at a given zoom level
 * This is an approximation for clustering purposes
 */
const getPixelDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
  zoom: number
): number => {
  // At zoom level, each degree of latitude is roughly 256 * 2^zoom pixels / 360
  const pixelsPerDegree = (256 * Math.pow(2, zoom)) / 360;

  // Calculate distance in degrees
  const latDiff = lat1 - lat2;
  const lngDiff = lng1 - lng2;

  // Convert to pixels (approximation)
  const pixelLat = latDiff * pixelsPerDegree;
  const pixelLng = lngDiff * pixelsPerDegree * Math.cos((lat1 * Math.PI) / 180);

  return Math.sqrt(pixelLat * pixelLat + pixelLng * pixelLng);
};

/**
 * Cluster nearby friends based on pixel distance at current zoom level
 * @param friends Array of friends with coordinates
 * @param zoom Current map zoom level
 * @param clusterRadius Maximum pixel distance to consider markers as "nearby" (default: 60px)
 * @returns Array of clusters (single friends or grouped friends)
 */
export const clusterFriends = (
  friends: FriendLocation[],
  zoom: number,
  clusterRadius: number = 60
): MarkerCluster[] => {
  const clusters: MarkerCluster[] = [];
  const processed = new Set<number>();

  // Disable clustering at high zoom levels (15+) - show all markers individually
  const disableClustering = zoom >= 15;

  // First, detect and apply jitter to overlapping coordinates
  const friendsWithJitter = friends.map(friend => {
    if (!friend.latitude || !friend.longitude) return friend;

    const lat = typeof friend.latitude === 'string' ? parseFloat(friend.latitude) : friend.latitude;
    const lng = typeof friend.longitude === 'string' ? parseFloat(friend.longitude) : friend.longitude;

    if (isNaN(lat) || isNaN(lng)) return friend;

    // Check if this friend's location overlaps with any other friend
    const hasOverlap = friends.some(other => {
      if (other.id === friend.id) return false;
      if (!other.latitude || !other.longitude) return false;

      const otherLat = typeof other.latitude === 'string' ? parseFloat(other.latitude) : other.latitude;
      const otherLng = typeof other.longitude === 'string' ? parseFloat(other.longitude) : other.longitude;

      if (isNaN(otherLat) || isNaN(otherLng)) return false;

      return areCoordinatesIdentical(lat, lng, otherLat, otherLng);
    });

    // Apply jitter if there's overlap
    if (hasOverlap) {
      const [jitteredLat, jitteredLng] = applyJitter(lat, lng, friend.id);
      return {
        ...friend,
        displayLatitude: jitteredLat,
        displayLongitude: jitteredLng,
      };
    }

    return friend;
  });

  // If clustering is disabled (high zoom), create individual clusters for each friend
  if (disableClustering) {
    return friendsWithJitter
      .filter(friend => {
        if (!friend.latitude || !friend.longitude) return false;
        const lat = friend.displayLatitude || (typeof friend.latitude === 'string' ? parseFloat(friend.latitude) : friend.latitude);
        const lng = friend.displayLongitude || (typeof friend.longitude === 'string' ? parseFloat(friend.longitude) : friend.longitude);
        return !isNaN(lat) && !isNaN(lng);
      })
      .map(friend => {
        const lat = friend.displayLatitude || (typeof friend.latitude === 'string' ? parseFloat(friend.latitude) : friend.latitude!);
        const lng = friend.displayLongitude || (typeof friend.longitude === 'string' ? parseFloat(friend.longitude) : friend.longitude!);

        return {
          id: `cluster-${friend.id}-${lat.toFixed(4)}-${lng.toFixed(4)}`,
          friends: [friend],
          latitude: lat as number,
          longitude: lng as number,
          center: [lat, lng] as [number, number],
        };
      });
  }

  friendsWithJitter.forEach((friend, index) => {
    if (processed.has(index)) return;
    if (!friend.latitude || !friend.longitude) return;

    // Use display coordinates if available, otherwise use original
    const friendLat = friend.displayLatitude || (typeof friend.latitude === 'string' ? parseFloat(friend.latitude) : friend.latitude);
    const friendLng = friend.displayLongitude || (typeof friend.longitude === 'string' ? parseFloat(friend.longitude) : friend.longitude);

    if (isNaN(friendLat) || isNaN(friendLng)) return;

    // Start a new cluster with this friend
    const cluster: FriendLocation[] = [friend];
    processed.add(index);

    // Find all nearby friends
    friendsWithJitter.forEach((otherFriend, otherIndex) => {
      if (processed.has(otherIndex)) return;
      if (!otherFriend.latitude || !otherFriend.longitude) return;

      // Use display coordinates if available, otherwise use original
      const otherLat = otherFriend.displayLatitude || (typeof otherFriend.latitude === 'string' ? parseFloat(otherFriend.latitude) : otherFriend.latitude);
      const otherLng = otherFriend.displayLongitude || (typeof otherFriend.longitude === 'string' ? parseFloat(otherFriend.longitude) : otherFriend.longitude);

      if (isNaN(otherLat) || isNaN(otherLng)) return;

      const distance = getPixelDistance(
        friendLat,
        friendLng,
        otherLat,
        otherLng,
        zoom
      );

      if (distance <= clusterRadius) {
        cluster.push(otherFriend);
        processed.add(otherIndex);
      }
    });

    // Calculate cluster center (average position)
    // Filter out any friends without valid coordinates (safety check)
    // Also convert strings to numbers if needed (database may return DECIMAL as string)
    const validFriends = cluster.filter(f => {
      const lat = f.displayLatitude || (typeof f.latitude === 'string' ? parseFloat(f.latitude) : f.latitude);
      const lng = f.displayLongitude || (typeof f.longitude === 'string' ? parseFloat(f.longitude) : f.longitude);
      return lat != null && lng != null && !isNaN(lat) && !isNaN(lng);
    });

    if (validFriends.length === 0) return; // Skip if no valid coordinates

    // Calculate center using display coordinates, ensuring values are numbers
    const centerLat = validFriends.reduce((sum, f) => {
      const lat = f.displayLatitude || (typeof f.latitude === 'string' ? parseFloat(f.latitude) : f.latitude!);
      return sum + lat;
    }, 0) / validFriends.length;

    const centerLng = validFriends.reduce((sum, f) => {
      const lng = f.displayLongitude || (typeof f.longitude === 'string' ? parseFloat(f.longitude) : f.longitude!);
      return sum + lng;
    }, 0) / validFriends.length;

    // Final safety check
    if (isNaN(centerLat) || isNaN(centerLng)) {
      console.warn('Invalid cluster center calculated, skipping cluster:', cluster);
      console.warn('CenterLat:', centerLat, 'CenterLng:', centerLng);
      console.warn('Valid friends:', validFriends.map(f => ({ id: f.id, lat: f.latitude, lng: f.longitude })));
      return;
    }

    // Create unique cluster ID by combining friend IDs and coordinates
    // This prevents duplicate keys when same friend has multiple temp locations
    const clusterIdParts = validFriends.map(f => {
      const lat = typeof f.latitude === 'string' ? parseFloat(f.latitude) : f.latitude!;
      const lng = typeof f.longitude === 'string' ? parseFloat(f.longitude) : f.longitude!;
      // Round to 4 decimal places for stability (~11m precision)
      return `${f.id}-${lat.toFixed(4)}-${lng.toFixed(4)}`;
    }).sort().join('_');

    clusters.push({
      id: `cluster-${clusterIdParts}`,
      friends: validFriends,
      latitude: centerLat,
      longitude: centerLng,
      center: [centerLat, centerLng],
    });
  });

  return clusters;
};

/**
 * Calculate the best zoom level to separate clustered markers
 * @param cluster The cluster to zoom into
 * @param currentZoom Current map zoom level
 * @param clusterRadius The clustering radius in pixels (default: 60px)
 * @returns Suggested zoom level
 */
export const calculateZoomForCluster = (
  cluster: MarkerCluster,
  currentZoom: number,
  clusterRadius: number = 60
): number => {
  if (cluster.friends.length === 1) {
    return currentZoom;
  }

  // Find the minimum distance between any two points in the cluster
  // We need to ensure even the closest points are separated
  let minDistance = Infinity;
  for (let i = 0; i < cluster.friends.length; i++) {
    for (let j = i + 1; j < cluster.friends.length; j++) {
      const f1 = cluster.friends[i];
      const f2 = cluster.friends[j];
      if (!f1.latitude || !f1.longitude || !f2.latitude || !f2.longitude) continue;

      // Convert to numbers if needed
      const lat1 = typeof f1.latitude === 'string' ? parseFloat(f1.latitude) : f1.latitude;
      const lng1 = typeof f1.longitude === 'string' ? parseFloat(f1.longitude) : f1.longitude;
      const lat2 = typeof f2.latitude === 'string' ? parseFloat(f2.latitude) : f2.latitude;
      const lng2 = typeof f2.longitude === 'string' ? parseFloat(f2.longitude) : f2.longitude;

      if (isNaN(lat1) || isNaN(lng1) || isNaN(lat2) || isNaN(lng2)) continue;

      const distance = getPixelDistance(lat1, lng1, lat2, lng2, currentZoom);
      minDistance = Math.min(minDistance, distance);
    }
  }

  // If no valid distances found, just zoom in a bit
  if (minDistance === Infinity) {
    return Math.min(currentZoom + 2, 18);
  }

  // Calculate how many zoom levels needed to separate markers
  // Target: at least 100px separation (clusterRadius + 40px margin)
  const targetSeparation = clusterRadius + 40;

  // Each zoom level doubles the pixel distance
  // So we need to find: 2^n * minDistance >= targetSeparation
  // => n >= log2(targetSeparation / minDistance)
  const zoomLevelsNeeded = Math.ceil(Math.log2(targetSeparation / minDistance));

  // Add the calculated zoom levels, with a minimum of +1 and maximum of 18
  const newZoom = Math.min(currentZoom + Math.max(1, zoomLevelsNeeded), 18);

  return newZoom;
};
